<script lang="ts" setup>
import type { IProps } from './TextWithImage.types'

defineProps<IProps>()

</script>

<template>
  <UiSection>
    <div class="text-with-image">
      <UiHeader
        v-if="!headerInColumn"
        :header="header"
        :subheader="subheader"
      />

      <div
        :class="[
          'text-with-image__layout',
          `text-with-image__layout--${layout}`
        ]"
      >
        <img
          class="text-with-image__img"
          :src="image"
        >

        <div class="text-with-image__content">
          <UiHeader
            v-if="headerInColumn"
            :header="header"
            :subheader="subheader"
            :position="position"
          />
          <div class="text-with-image__text-content">
            <img
              v-if="icon"
              :src="icon"
              width="40"
            >
            <div>
              <p
                v-if="text"
                class="text-with-image__text text-with-image__text--first"
              >
                {{ text }}
              </p>

              <div
                v-if="textSecond"
                class="text-with-image__text text-with-image__text--second"
                v-html="textSecond"
              />
            </div>
          </div>

          <UiList
            v-if="list"
            :list="list"
          />

          <div class="text-with-image__actions">
            <UiButton
              v-if="button"
              :to="button.to"
              :variant="button.variant || 'primary'"
              :target="button.target || '_self'"
            >
              <span
                v-if="button.icon"
                class="button__icon"
              >
                <img
                  class="button__img"
                  width="24px"
                  height="24px"
                  alt=""
                  :src="button.icon"
                >
              </span>

              {{ button.text }}
            </UiButton>
            <UiButton
              v-if="button2"
              :to="button2.to"
              :variant="button2.variant || 'secondary'"
              :target="button2.target || '_self'"
            >
              <span
                v-if="button2.icon"
                class="button__icon"
              >
                <img
                  class="button__img"
                  width="24px"
                  height="24px"
                  alt=""
                  :src="button2.icon"
                >
              </span>

              {{ button2.text }}
            </UiButton>
          </div>
        </div>
      </div>

      <p
        v-if="textThird"
        class="text-with-image__extra"
      >
        {{ textThird }}
      </p>

      <p
        v-if="textThirdExtra"
        class="text-with-image__extra text-with-image__extra--small"
      >
        {{ textThirdExtra }}
      </p>
    </div>
  </UiSection>
</template>

<style lang="scss" scoped>
.text-with-image {
  $root: &;

  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 115.2rem;
  width: 100%;
  margin: 0 auto;

  @include atXL {
    max-width: 134rem;
  }

  &__layout {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 3.2rem;

    @include atM {
      flex-direction: row;
      align-items: flex-start;
      gap: 8rem;
    }

    &--default {
      @include atM {
        margin-top: 9rem;
      }

      #{$root}__content {
        @include atM {
          max-width: 56rem;
        }
      }

      #{$root}__img {
        width: 80%;

        @include atM {
          max-width: 53rem;
        }
      }
    }

    &--layout-1,
    &--layout-2,
    &--layout-3,
    &--layout-4 {

      #{$root}__img {
        border-radius: 1.6rem;
      }
    }

    &--layout-2,
    &--layout-3 {

      #{$root}__img {
        height: 22rem;
        object-fit: cover;
        object-position: center;

        @include atXS {
          height: auto;
        }
      }
    }

    &--layout-1 {

      @include atM {
        gap: 9.7rem;
        margin-top: 6rem;
      }

      #{$root}__img,
      #{$root}__content {

        @include atM {
          width: 50%;
        }
      }

      #{$root}__text {
        &--first {
          @include atM {
            margin-bottom: 0;
            font-size: 2rem;
            line-height: 2.8rem;
          }

          @include atXL {
            font-size: 2.8rem;
            line-height: 3.6rem;
          }
        }
      }

      @include atXL {
        gap: 13.4rem;
      }
    }

    &--layout-2 {

      @include atM {
        margin-top: 4.8rem;
      }
    }

    &--layout-3 {

      @include atM {
        gap: 7.8rem;
      }

      #{$root}__text {
        margin-top: 1.6rem;

        @include atM {
          margin-top: 3.2rem;
          margin-bottom: 0;
        }
      }
    }

    &--layout-4 {
      #{$root}__img {
        height: 33rem;
        margin-bottom: 2.4rem;
        object-fit: cover;
        object-position: center;

        @include atXS {
          height: 60rem;
        }

        @include atXL {
          height: auto;
        }
      }

      @include atM {
        flex-direction: row-reverse;
        align-items: center;
        gap: 5.6rem;

        #{$root}__content {
          min-width: 50%;
        }
      }

      @include atXL {
        gap: 13.2rem;

        #{$root}__content {
          min-width: 53.8rem;
        }
      }
    }

  }

  &__content {
    display: flex;
    flex-direction: column;

    .button {
      align-self: center;

      &__icon {
        display: inline-block;
        padding-right: 1.2rem;

        img {
          width: 24px;
          height: 24px;
        }
      }

      @include atM {
        align-self: flex-start;
      }
    }
  }

  &__text-content {
    display: flex;
    align-items: flex-start;
    gap: 3.5rem;

    @include atXS {
      flex-direction: column;
    }
  }

  &__img {
    width: 100%;
    margin-bottom: 3.2rem;

    @include atM {
      margin-bottom: 0;
    }
  }

  &__text {
    font-weight: 500;
  }

  &__text--first {
    margin-bottom: 1.6rem;
    font-size: 2rem;
    line-height: 2.8rem;

    @include atM {
      margin-bottom: 3.8rem;
      font-size: 2rem;
      line-height: 2.8rem;
    }

    @include atXL {
      font-size: 2.8rem;
      line-height: 3.6rem;
    }
  }

  &__text--second {
    margin-bottom: 3.2rem;
    font-weight: 500;
    font-size: 1.6rem;
    line-height: 2.4rem;

    :deep() {
      span {
        color: var(--span-color)
      }
    }

    @include atM {
      margin-bottom: 5.6rem;
      font-size: 1.8rem;
      line-height: 2.4rem;
    }

    @include atXL {
      font-size: 2rem;
      line-height: 3.2rem;
    }
  }

  &__extra {
    margin-top: 4rem;
    font-weight: 500;
    font-size: 2rem;
    line-height: 2.8rem;
    white-space: pre-line;
    text-align: center;

    @include atM {
      max-width: 110rem;
      margin-top: 12rem;
      font-size: 3.2rem;
      line-height: 4rem;
    }

    @include atXL {
      font-size: 4rem;
      line-height: 4.8rem;
    }

    &--small {
      margin-top: 2rem;
      color: $text-200;
      font-size: 1.6rem;
      line-height: 2.4rem;

      @include atM {
        font-size: 2rem;
        line-height: 2.8rem;
      }
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;

    @include atM {
      flex-direction: row;
      gap: 0.8rem;
    }
  }
}
</style>
